import { GanttTask, Dependency } from '../types/gantt.types'
import {
  CascadePlan, getCascadeIds, computeCascadeReschedule, holidayGateError, movedRange, resizedRange, type HolidayGate,
} from '../views/scheduleUtils'
import { useState, useRef, useEffect, useCallback } from 'react'
import { addDays, daysBetween } from '../constants/gantt.logic'
// 막대 이동·리사이즈(문서 단위 마우스 리스너) — 이동 전 GanttView.tsx 본문을 옮긴 것이다.
// A-09~A-11: 놓는 순간 소요일(작업일 수)을 유지·재계산하고 비작업일 시작은 다음 작업일로 보정한다.
// 이동·리사이즈 중에는 이동 가능 범위(표시 기간 앞뒤 3일, movableRangeRef)를 넘지 않도록 오프셋을 제한하며 표시 범위를 넓히지 않는다.
// 문서 단위 리스너는 [] 의존이라 첫 렌더 함수·ref 만 읽는다 — state 값을 직접 읽게 바꾸지 말 것.

export type UseGanttBarDragArgs = {
  DWRef: React.MutableRefObject<number>
  tasksRef: React.MutableRefObject<GanttTask[]>
  /** 이동 가능 범위(표시 기간 앞뒤 3일) — 끌기·리사이즈로 넓어지지 않는다(A-11) */
  movableRangeRef: React.MutableRefObject<{ start: Date; end: Date }>
  /** 공휴일 조회 상태·범위 — 조회 중·실패·범위 밖이면 날짜를 계산하지 않는다(A-08) */
  holidayGateRef: React.MutableRefObject<HolidayGate>
  /** 막대 클릭 — 열린 공종이면 닫고 다른 공종이면 전환(B-15, 이탈 확인 포함) */
  togglePanelTaskGuarded: (taskId: number) => void
  depsRef: React.MutableRefObject<Dependency[]>
  dragErrorTimer: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
  setDragError: React.Dispatch<React.SetStateAction<string>>
  setPullConfirm: React.Dispatch<React.SetStateAction<{ taskId: number; taskName: string; newStart: Date; newEnd: Date; direction: "pull" | "push"; } | null>>
  applyCascadePlan: (plan: CascadePlan, opts?: { syncOrderDates?: boolean }) => boolean
  holidaysRef: React.MutableRefObject<Set<string>>
  setCtxMenu: React.Dispatch<React.SetStateAction<{ x: number; y: number; taskId: number; taskName: string; taskEndDate: Date; } | null>>
  setSelectedDepId: React.Dispatch<React.SetStateAction<number | null>>
}

export function useGanttBarDrag({
  DWRef, tasksRef, movableRangeRef, holidayGateRef, togglePanelTaskGuarded,
  depsRef, dragErrorTimer, setDragError, setPullConfirm, applyCascadePlan, holidaysRef, setCtxMenu,
  setSelectedDepId,
}: UseGanttBarDragArgs) {
  const [draggingTaskId,   setDraggingTaskId]   = useState<number | null>(null)
  const [draggingOffset,   setDraggingOffset]   = useState(0)
  // 바 오른쪽 끝을 드래그해 소요일(종료일)만 늘리거나 줄이는 리사이즈 — 시작일은 고정.
  const [resizingTaskId,   setResizingTaskId]   = useState<number | null>(null)
  const [resizingOffset,   setResizingOffset]   = useState(0)
  const barDragState = useRef<{
    taskId:        number
    startX:        number
    currentOffset: number
  } | null>(null)

  // barResizeState: 바 오른쪽 끝 리사이즈 — startDate는 불변, offset은 종료일에만 더해진다.
  const barResizeState = useRef<{
    taskId:        number
    startX:        number
    currentOffset: number
  } | null>(null)

  // 오류줄 표시(3.5초) — ref·setter 만 쓴다
  const showErr = (msg: string) => {
    if (dragErrorTimer.current) clearTimeout(dragErrorTimer.current)
    setDragError(msg)
    dragErrorTimer.current = setTimeout(() => setDragError(''), 3500)
  }

  // ── Document-level mouse listeners ────────────────────────────
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = barDragState.current
      if (!drag) return
      const dx        = e.clientX - drag.startX
      let newOffset   = Math.round(dx / DWRef.current)

      // 이동 가능 범위(표시 기간 앞뒤 3일) 안으로 제한 — 표시 범위를 넓히지 않는다(A-11)
      const task = tasksRef.current.find(t => t.taskId === drag.taskId)
      if (task) {
        const mr = movableRangeRef.current
        const lo = Math.min(0, daysBetween(task.startDate, mr.start))
        const hi = Math.max(0, daysBetween(task.endDate, mr.end))
        newOffset = Math.min(Math.max(newOffset, lo), hi)
      }

      if (newOffset !== drag.currentOffset) {
        drag.currentOffset = newOffset
        setDraggingOffset(newOffset)
      }
    }

    const onUp = () => {
      const drag = barDragState.current
      if (!drag) return

      const taskId    = drag.taskId
      const dayOffset = drag.currentOffset

      barDragState.current = null
      document.body.style.cursor    = ''
      document.body.style.userSelect = ''
      setDraggingTaskId(null)
      setDraggingOffset(0)

      if (dayOffset === 0) {
        togglePanelTaskGuarded(taskId)
        return
      }

      const currentTasks = tasksRef.current
      const currentDeps  = depsRef.current
      const task = currentTasks.find(t => t.taskId === taskId)
      if (!task) return

      // 공휴일 조회 상태·범위 확인 — 조회 중·실패·범위 밖이면 계산하지 않는다(A-08)
      const preErr = holidayGateError(holidayGateRef.current, task.startDate, task.endDate,
        addDays(task.startDate, dayOffset), addDays(task.endDate, dayOffset))
      if (preErr) { showErr(preErr); return }

      // 놓는 순간 확정: 시작일이 일요일·공휴일이면 다음 작업일로 보정, 소요일(작업일 수)은 유지(A-09·A-10)
      const moved = movedRange(task.startDate, task.endDate, dayOffset, holidaysRef.current)
      const newStart = moved.start
      const newEnd   = moved.end
      const endErr = holidayGateError(holidayGateRef.current, newStart, newEnd)
      if (endErr) { showErr(endErr); return }
      // 보정 결과가 제자리면(예: 토요일 막대를 일요일로 끌어 다음 월요일로 보정된 결과가 같은 경우) 변경 없음
      if (newStart.getTime() === task.startDate.getTime() && newEnd.getTime() === task.endDate.getTime()) return

      if (dayOffset < 0) {
        // 과거 날짜로 당기는 이동만 차단
        const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0)
        if (newStart < todayMidnight) {
          showErr('현재 날짜 이전으로는 변경할 수 없습니다.')
          return
        }

        // 당기기(시작일이 빨라짐) — 후행공정이 있으면 함께 이동할지 확인창을 띄우고 대기
        if (getCascadeIds(taskId, currentDeps).length > 1) {
          setPullConfirm({ taskId, taskName: task.workTypeName, newStart, newEnd, direction: 'pull' })
          return
        }

        applyCascadePlan(new Map([[taskId, { startDate: newStart, endDate: newEnd }]]))
        return
      }

      // 밀기(뒤로) — 당기기와 동일하게, 후행공정이 있으면 함께 이동할지 확인창을 띄우고 대기
      if (getCascadeIds(taskId, currentDeps).length > 1) {
        setPullConfirm({ taskId, taskName: task.workTypeName, newStart, newEnd, direction: 'push' })
        return
      }

      applyCascadePlan(new Map([[taskId, { startDate: newStart, endDate: newEnd }]]))
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup',   onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup',   onUp)
    }
  }, [])

  // ── Document-level mouse listeners (바 오른쪽 끝 리사이즈) ─────
  // 시작일은 그대로 두고 종료일(소요일)만 늘리거나 줄인다 — handleRowDurationChange의
  // ▲▼ 스피너와 같은 캐스케이드 규칙(push-if-violated)을 그대로 쓰되, 드래그 중 실시간으로
  // 얼마나 늘어나는지 보여준다는 점만 다르다.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const rz = barResizeState.current
      if (!rz) return
      const dx = e.clientX - rz.startX
      let newOffset = Math.round(dx / DWRef.current)

      const task = tasksRef.current.find(t => t.taskId === rz.taskId)
      if (task) {
        const duration = daysBetween(task.startDate, task.endDate) + 1
        // 최소 1일은 유지되도록 offset 하한을 건다(그 이상 줄어들지 않음)
        newOffset = Math.max(newOffset, -(duration - 1))
        // 이동 가능 범위(표시 기간 앞뒤 3일) 오른쪽을 넘지 않게 제한 — 표시 범위를 넓히지 않는다(A-11)
        const hi = Math.max(0, daysBetween(task.endDate, movableRangeRef.current.end))
        newOffset = Math.min(newOffset, hi)
      }

      if (newOffset !== rz.currentOffset) {
        rz.currentOffset = newOffset
        setResizingOffset(newOffset)
      }
    }

    const onUp = () => {
      const rz = barResizeState.current
      if (!rz) return

      const taskId    = rz.taskId
      const dayOffset = rz.currentOffset

      barResizeState.current = null
      document.body.style.cursor     = ''
      document.body.style.userSelect = ''
      setResizingTaskId(null)
      setResizingOffset(0)

      if (dayOffset === 0) return

      const currentTasks = tasksRef.current
      const currentDeps  = depsRef.current
      const task = currentTasks.find(t => t.taskId === taskId)
      if (!task) return

      // 공휴일 조회 상태·범위 확인(A-08) 후, 오프셋을 작업일 증감으로 해석한다(최소 1작업일, A-09)
      const preErr = holidayGateError(holidayGateRef.current, task.startDate, task.endDate, addDays(task.endDate, dayOffset))
      if (preErr) { showErr(preErr); return }
      const rr = resizedRange(task.startDate, task.endDate, dayOffset, holidaysRef.current)
      const endErr = holidayGateError(holidayGateRef.current, rr.start, rr.end)
      if (endErr) { showErr(endErr); return }
      if (rr.start.getTime() === task.startDate.getTime() && rr.end.getTime() === task.endDate.getTime()) return

      const plan = computeCascadeReschedule(taskId, rr.start, rr.end, currentTasks, currentDeps, holidaysRef.current, 'push-if-violated')
      applyCascadePlan(plan)
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup',   onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup',   onUp)
    }
  }, [])

  // ── 바 즉시 드래그 시작 ────────────────────────────────────────
  const onBarMouseDown = useCallback((e: React.MouseEvent, taskId: number) => {
    if (e.button !== 0) return
    setCtxMenu(null)
    setSelectedDepId(null)
    e.stopPropagation()
    barDragState.current = { taskId, startX: e.clientX, currentOffset: 0 }
    document.body.style.cursor    = 'ew-resize'
    document.body.style.userSelect = 'none'
    setDraggingTaskId(taskId)
  }, [])

  // ── 바 오른쪽 끝 리사이즈 드래그 시작 ──────────────────────────
  const onBarResizeMouseDown = useCallback((e: React.MouseEvent, taskId: number) => {
    if (e.button !== 0) return
    e.stopPropagation()
    setCtxMenu(null)
    barResizeState.current = { taskId, startX: e.clientX, currentOffset: 0 }
    document.body.style.cursor     = 'ew-resize'
    document.body.style.userSelect = 'none'
    setResizingTaskId(taskId)
  }, [])

  return {
    barDragState, barResizeState, draggingTaskId, draggingOffset, resizingTaskId, resizingOffset,
    onBarMouseDown, onBarResizeMouseDown,
  }
}
